import React, { useState } from "react";
import FileUpload from "./FileUpload";
import Button from "@mui/material/Button";
import BlockNameInput from "./BlockNameInput";
import ConfiguredFilesList from "./ConfiguredFilesList";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import { toast } from 'react-toastify';

import axios from "axios";
import { BlobServiceClient } from "@azure/storage-blob";
import API_GATEWAY_URL from "../../config";
import "./DataUpload.css";
import { findAllInRenderedTree } from "react-dom/test-utils";

const STORAGE_CONTAINER = "01-data";
const STORAGE_SUBDIRECTORY = {
  "Orthomosaic": "raster/orthomosaic",
  "Boundary": "vector/boundary",
  "Palm Location": "vector/palm_location",
  "Palm Canopy Size": "vector/palm_canopy_size",
  "Palm Distance": "vector/palm_distance",
  "Palm Yellowish Classification": "vector/palm_yellowish_classification"
};

function DataUpload() {
  const [selectedBlock, setSelectedBlock] = useState("");
  const [selectedEstate, setSelectedEstate] = useState("");
  const [targetEmail, setTargetEmail] = useState("");
  const [configuredFiles, setConfiguredFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [fileStatuses, setFileStatuses] = useState({});
  const [abortController, setAbortController] = useState(null);

  const handleSubmit = async () => {
    if (selectedBlock === "" || selectedEstate === "") {
      toast.error("Please enter your estate name and block name");
      return;
    }

    setUploading(true);

    const controller = new AbortController();
    setAbortController(controller);

    const initialStatuses = {};
    configuredFiles.forEach((item) => {
      item.files.forEach((file) => {
        initialStatuses[file.name] = { status: "Pending", progress: 0 };
      });
    });
    setFileStatuses(initialStatuses);

    const jwt_token = localStorage.getItem("palm_care_token");
    const email = localStorage.getItem("palm_care_email");
    const group = localStorage.getItem("palm_care_group");
    const apiUrl = `${API_GATEWAY_URL}/sas/upload`;
    const headers = {
      Authorization: jwt_token,
    };

    try {
      const response = await axios.get(apiUrl, {
        headers,
        params: {
          container: STORAGE_CONTAINER,
        },
      });

      const parsedUrl = new URL(response.data.sas_url);
      const blob_storage_sas_url = `${parsedUrl.protocol}//${parsedUrl.host}${parsedUrl.search}`;
      const blobServiceClient = new BlobServiceClient(blob_storage_sas_url);
      const containerClient = blobServiceClient.getContainerClient(STORAGE_CONTAINER);

      const uploadTargetEmail = targetEmail || email;
      const uploadBlockDirectory = `${uploadTargetEmail}/${selectedEstate}/${selectedBlock}`;
      for (const item of configuredFiles) {
        for (const file of item.files) {
          const fileExt = file.name.split(".").pop();
          let customSuffix = "";
          if (item.files_type === "Orthomosaic") customSuffix = "_ortho";
          else if (item.files_type === "Boundary") customSuffix = "_boundary";

          const blobName = `${uploadBlockDirectory}/${STORAGE_SUBDIRECTORY[item.files_type]}/${formatDate(
            item.created_date
          )}${customSuffix}.${fileExt}`;
          const blobClient = containerClient.getBlockBlobClient(blobName);

          setFileStatuses((prev) => ({
            ...prev,
            [file.name]: { status: "Uploading", progress: 0 },
          }));

          const uploadPromise = blobClient.uploadBrowserData(file, {
            abortSignal: controller.signal,
            onProgress: (ev) => {
              setFileStatuses((prev) => ({
                ...prev,
                [file.name]: { status: "Uploading", progress: (ev.loadedBytes / file.size) * 100 },
              }));
            }
          });
          await uploadPromise;

          setFileStatuses((prev) => ({
            ...prev,
            [file.name]: { status: "Completed", progress: 100 },
          }));

          const metadata = {
            username: email.split("@")[0],
            domain: email.split("@")[1],
            group: group,
            upload_date: new Date().toISOString(),
          };
          await blobClient.setMetadata(metadata);
        }
      }
      toast.success("Data successfully uploaded to storage");
      setConfiguredFiles([]);
    } catch (error) {
      if (error.name === "AbortError" || (error.message && error.message.toLowerCase().includes("abort"))) {
        toast.info("Upload cancelled");
        // Mark remaining pending/uploading as cancelled
        setFileStatuses((prev) => {
          const newStatuses = { ...prev };
          Object.keys(newStatuses).forEach((key) => {
            if (newStatuses[key].status === "Pending" || newStatuses[key].status === "Uploading") {
              newStatuses[key].status = "Cancelled";
            }
          });
          return newStatuses;
        });
      } else {
        console.error("Error uploading data:", error);
        toast.error("Error uploading data");
      }
    } finally {
      setUploading(false);
      setAbortController(null);
    }
  };

  function formatDate(inputDateString) {
    const inputDate = new Date(inputDateString);
    const year = inputDate.getFullYear();
    const month = (inputDate.getMonth() + 1).toString().padStart(2, "0");
    const day = inputDate.getDate().toString().padStart(2, "0");
    return `${year}${month}${day}`;
  }

  return (
    <div className="data__upload__container">
      <div className="data__upload__block__selection">
        <BlockNameInput
          label="Block Name"
          filter_type="block"
          selectedValue={selectedBlock}
          setSelectedValue={setSelectedBlock}
        />
        <BlockNameInput
          label="Estate Name"
          filter_type="estate"
          selectedValue={selectedEstate}
          setSelectedValue={setSelectedEstate}
          setTargetEmail={setTargetEmail}
        />
      </div>
      <FileUpload configuredFiles={configuredFiles} setConfiguredFiles={setConfiguredFiles} />
      {configuredFiles.length > 0 && (
        <div>
          <div className="data__upload__section__3">
            <ConfiguredFilesList configuredFiles={configuredFiles} setConfiguredFiles={setConfiguredFiles} />
          </div>
          <div>
            <div style={{ display: "flex", gap: "10px" }}>
              <Button
                style={{
                  backgroundColor: "#1976d2",
                  color: "white",
                  fontSize: "15px",
                  fontWeight: 600,
                  width: "100%",
                  padding: "10px",
                }}
                onClick={handleSubmit}
                disabled={uploading}
              >
                {uploading ? <CircularProgress size={24} color="inherit" /> : "Submit"}
              </Button>
              {uploading && (
                <Button
                  style={{
                    backgroundColor: "#d32f2f",
                    color: "white",
                    fontSize: "15px",
                    fontWeight: 600,
                    padding: "10px",
                    width: "150px"
                  }}
                  onClick={() => abortController && abortController.abort()}
                >
                  Cancel
                </Button>
              )}
            </div>
            {Object.keys(fileStatuses).length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <Typography variant="h6" style={{ marginBottom: '10px' }}>Upload Status</Typography>
                {Object.entries(fileStatuses).map(([fileName, statusInfo]) => (
                  <div key={fileName} style={{ marginBottom: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">{fileName}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {statusInfo.status} {statusInfo.status === "Uploading" ? `(${Math.round(statusInfo.progress)}%)` : ""}
                      </Typography>
                    </div>
                    <LinearProgress
                      variant="determinate"
                      value={statusInfo.progress}
                      color={
                        statusInfo.status === "Completed" ? "success" :
                          statusInfo.status === "Cancelled" ? "error" : "primary"
                      }
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DataUpload;
